import { useState, useEffect, useCallback, useRef } from 'react';
import type { TenantAdmin, TenantStatus } from '@yawi/types';
import type { PaginationMeta } from '@yawi/domain';
import { useServices, useToast } from '@/app/providers';

interface UseTenantsParams {
  page: number;
  limit: number;
  search?: string;
  status?: TenantStatus;
}

export function useTenants(params: UseTenantsParams) {
  const { tenantRepository } = useServices();
  const { showToast } = useToast();

  const [tenants, setTenants] = useState<TenantAdmin[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { page, limit, search, status } = params;

  // Only the latest request may write state: a slow response for an older
  // search/page must not overwrite the current one.
  const requestIdRef = useRef(0);

  const fetchTenants = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await tenantRepository.findAll({
        page,
        limit,
        search: search?.trim() || undefined,
        status,
      });
      if (requestId !== requestIdRef.current) return;
      setTenants(response.data);
      setMeta(response.meta);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load tenants';
      setError(message);
      showToast('error', message);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [tenantRepository, showToast, page, limit, search, status]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const suspendTenant = async (id: string) => {
    try {
      await tenantRepository.suspend(id);
      showToast('success', 'Tenant suspended successfully');
      await fetchTenants();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to suspend tenant';
      showToast('error', message);
    }
  };

  const activateTenant = async (id: string) => {
    try {
      await tenantRepository.activate(id);
      showToast('success', 'Tenant activated successfully');
      await fetchTenants();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to activate tenant';
      showToast('error', message);
    }
  };

  const deleteTenant = async (id: string) => {
    try {
      await tenantRepository.delete(id);
      showToast('success', 'Tenant deleted successfully');
      await fetchTenants();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete tenant';
      showToast('error', message);
    }
  };

  return {
    tenants,
    meta,
    isLoading,
    error,
    refetch: fetchTenants,
    suspendTenant,
    activateTenant,
    deleteTenant,
  };
}
